// A custom breakdown is an imported form question rather than a column on the source view, so it
// can't be named by a bare sourceKey. It travels through `WidgetConfig.breakdowns` as an encoded
// key so the config stays `string[]` end to end.
//
// Identity is the (questionName, questionType, sourceId) triple used by `insights_custom_dimension`
// and the available-breakdowns API. Parts are URI-encoded because a `::` inside a question name
// would otherwise shift every later part.
//
// A multi-level question additionally carries a 1-based `level` as an optional 4th part: the same
// question at two levels is one imported dimension used twice, so the level is part of the key, not
// of the identity.

const PREFIX = 'custom::'
const SEPARATOR = '::'

const LEVEL_PATTERN = /^[1-9]\d*$/

export type CustomBreakdownRef = {
  questionName: string
  questionType: string
  sourceId: string | null
  /** 1-based level into a multi-level answer's path; absent for flat questions. */
  level?: number
}

export const isCustomBreakdownKey = (key: string): boolean => key.startsWith(PREFIX)

export const encodeCustomBreakdown = ({ questionName, questionType, sourceId, level }: CustomBreakdownRef): string =>
  PREFIX +
  [questionName, questionType, sourceId ?? '', ...(level == null ? [] : [String(level)])].map(encodeURIComponent).join(SEPARATOR)

export const decodeCustomBreakdown = (key: string): CustomBreakdownRef | null => {
  if (!isCustomBreakdownKey(key)) return null
  const parts = key.slice(PREFIX.length).split(SEPARATOR)
  if (parts.length !== 3 && parts.length !== 4) return null
  // A malformed escape makes decodeURIComponent throw; a persisted config must never crash a render.
  try {
    const [questionName = '', questionType = '', sourceId = '', level] = parts.map((part) => decodeURIComponent(part))
    if (questionName === '' || questionType === '') return null
    if (level != null && !LEVEL_PATTERN.test(level)) return null
    return {
      questionName,
      questionType,
      sourceId: sourceId === '' ? null : sourceId,
      ...(level == null ? {} : { level: Number(level) }),
    }
  } catch {
    return null
  }
}
